<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { addDays, differenceInCalendarDays, subDays } from 'date-fns';
import Button from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ReportsAPI from 'dashboard/api/reports';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  savedReport: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['analysis', 'loading']);
const store = useStore();
const { t, tm } = useI18n();
const agents = useMapGetter('agents/getAgents');
const question = ref('');
const isLoading = ref(false);
const error = ref('');
const processingMessage = ref('');
const selectedRange = ref('question');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const customCalendarRangeType = ref('custom');
const criterion = ref('auto');
const comparison = ref('previous_period');
const comparisonAgentId = ref('');
const goal = ref('');
const isSuggestionsOpen = ref(false);
const activeSuggestionIndex = ref(-1);
const recentQuestions = ref([]);
const QUESTION_HISTORY_STORAGE_KEY = 'lumera-ai-question-history';
const MAX_RECENT_QUESTIONS = 6;
const MAX_ANALYTICAL_PERIOD_DAYS = 30;
const copy = {
  title: t('OVERVIEW_REPORTS.ANALYTICS.ASK_TITLE'),
  description: t('OVERVIEW_REPORTS.ANALYTICS.ASK_DESCRIPTION'),
  questionLabel: t('OVERVIEW_REPORTS.ANALYTICS.QUESTION_LABEL'),
  placeholder: t('OVERVIEW_REPORTS.ANALYTICS.QUESTION_PLACEHOLDER'),
  submit: t('OVERVIEW_REPORTS.ANALYTICS.SUBMIT'),
  unavailable: t('OVERVIEW_REPORTS.ANALYTICS.UNAVAILABLE'),
  processing: t('OVERVIEW_REPORTS.ANALYTICS.PROCESSING'),
  agentPrefix: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_PREFIX'),
  suggestionsTitle: t('OVERVIEW_REPORTS.ANALYTICS.SUGGESTIONS_TITLE'),
  recentQuestionsTitle: t('OVERVIEW_REPORTS.ANALYTICS.RECENT_QUESTIONS_TITLE'),
  recommendedQuestionsTitle: t(
    'OVERVIEW_REPORTS.ANALYTICS.RECOMMENDED_QUESTIONS_TITLE'
  ),
  removeRecentQuestion: t('OVERVIEW_REPORTS.ANALYTICS.REMOVE_RECENT_QUESTION'),
  completePerformance: t('OVERVIEW_REPORTS.ANALYTICS.COMPLETE_PERFORMANCE'),
  conversationsAndResolutions: t(
    'OVERVIEW_REPORTS.ANALYTICS.CONVERSATIONS_AND_RESOLUTIONS'
  ),
  responseTime: t('OVERVIEW_REPORTS.ANALYTICS.RESPONSE_TIME'),
  compareWithTeam: t('OVERVIEW_REPORTS.ANALYTICS.COMPARE_WITH_TEAM'),
  questionRange: t('OVERVIEW_REPORTS.ANALYTICS.QUESTION_RANGE'),
  last7Days: t('OVERVIEW_REPORTS.ANALYTICS.LAST_7_DAYS'),
  last30Days: t('OVERVIEW_REPORTS.ANALYTICS.LAST_30_DAYS'),
  customRange: t('OVERVIEW_REPORTS.ANALYTICS.CUSTOM_RANGE'),
  customDateHint: t('OVERVIEW_REPORTS.ANALYTICS.CUSTOM_DATE_HINT'),
  maximumPeriod: t('OVERVIEW_REPORTS.ANALYTICS.MAXIMUM_PERIOD'),
  criterion: t('OVERVIEW_REPORTS.ANALYTICS.CRITERION'),
  comparison: t('OVERVIEW_REPORTS.ANALYTICS.COMPARISON'),
  automatic: t('OVERVIEW_REPORTS.ANALYTICS.AUTOMATIC'),
  volume: t('OVERVIEW_REPORTS.ANALYTICS.VOLUME'),
  resolution: t('OVERVIEW_REPORTS.ANALYTICS.RESOLUTION'),
  responseTimeCriterion: t(
    'OVERVIEW_REPORTS.ANALYTICS.RESPONSE_TIME_CRITERION'
  ),
  satisfaction: t('OVERVIEW_REPORTS.ANALYTICS.SATISFACTION'),
  previousPeriod: t('OVERVIEW_REPORTS.ANALYTICS.PREVIOUS_PERIOD'),
  team: t('OVERVIEW_REPORTS.ANALYTICS.TEAM'),
  anotherAgent: t('OVERVIEW_REPORTS.ANALYTICS.ANOTHER_AGENT'),
  selectAgent: t('OVERVIEW_REPORTS.ANALYTICS.SELECT_AGENT'),
  goal: t('OVERVIEW_REPORTS.ANALYTICS.GOAL'),
  goalPlaceholder: t('OVERVIEW_REPORTS.ANALYTICS.GOAL_PLACEHOLDER'),
  noComparison: t('OVERVIEW_REPORTS.ANALYTICS.NO_COMPARISON'),
};

const quickSuggestions = tm('OVERVIEW_REPORTS.ANALYTICS.QUICK_SUGGESTIONS');
const rangeOptions = [
  { value: 'question', label: copy.questionRange },
  { value: '7', label: copy.last7Days },
  { value: '30', label: copy.last30Days },
  { value: 'custom', label: copy.customRange },
];
const criterionOptions = [
  { value: 'auto', label: copy.automatic },
  { value: 'volume', label: copy.volume },
  { value: 'resolution', label: copy.resolution },
  { value: 'response_time', label: copy.responseTimeCriterion },
  { value: 'satisfaction', label: copy.satisfaction },
];
const comparisonOptions = [
  { value: 'previous_period', label: copy.previousPeriod },
  { value: 'team', label: copy.team },
  { value: 'agent', label: copy.anotherAgent },
  { value: 'none', label: copy.noComparison },
];
const operationQuestionForRange = days =>
  t('OVERVIEW_REPORTS.ANALYTICS.OPERATION_QUESTION', { days });

const normalize = value =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
const periodFromQuestion = value => {
  const normalizedValue = normalize(value);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  const days = normalizedValue.match(
    /\b(?:(?:nos?\s+)?ultimos?|de|por|em)?\s*(\d{1,3})\s+dias?\b/
  )?.[1];

  if (days && Number(days) > 0) {
    start.setDate(start.getDate() - (Number(days) - 1));
  } else if (/\bhoje\b/.test(normalizedValue)) {
    // O início já é o dia atual.
  } else if (/\bontem\b/.test(normalizedValue)) {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (/\besta\s+semana\b/.test(normalizedValue)) {
    start.setDate(start.getDate() - start.getDay());
  } else if (/\bsemana\s+passada\b/.test(normalizedValue)) {
    start.setDate(start.getDate() - start.getDay() - 7);
    end.setDate(end.getDate() - end.getDay() - 1);
  } else {
    return {};
  }

  start.setHours(0, 0, 0, 0);
  return {
    since: Math.floor(start.getTime() / 1000),
    until: Math.floor(end.getTime() / 1000),
  };
};
const periodFromRange = () => {
  if (selectedRange.value === 'question') {
    return periodFromQuestion(question.value);
  }

  if (selectedRange.value === 'custom') {
    const [startDate, endDate] = customDateRange.value;
    if (!startDate || !endDate) return {};

    const since = new Date(startDate);
    const until = new Date(endDate);
    since.setHours(0, 0, 0, 0);
    until.setHours(23, 59, 59, 999);
    return {
      since: Math.floor(since.getTime() / 1000),
      until: Math.floor(until.getTime() / 1000),
    };
  }

  const days = Number(selectedRange.value);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return {
    since: Math.floor(start.getTime() / 1000),
    until: Math.floor(end.getTime() / 1000),
  };
};
const selectRange = range => {
  selectedRange.value = range;
};
const normalizeCustomDateRange = value => {
  const [startDate, endDate] = value;
  if (
    startDate &&
    endDate &&
    differenceInCalendarDays(endDate, startDate) >= MAX_ANALYTICAL_PERIOD_DAYS
  ) {
    return [startDate, addDays(startDate, MAX_ANALYTICAL_PERIOD_DAYS - 1)];
  }

  return value;
};
const onCustomDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = normalizeCustomDateRange([startDate, endDate]);
  customCalendarRangeType.value = rangeType || 'custom';
};
const exceedsMaximumPeriod = filters => {
  if (!filters.since || !filters.until) return false;

  return filters.until - filters.since > MAX_ANALYTICAL_PERIOD_DAYS * 86_400;
};
const normalizedQuestion = computed(() => normalize(question.value).trim());
const visibleQuickSuggestions = computed(() => {
  const currentRange = /^\d+$/.test(selectedRange.value)
    ? selectedRange.value
    : '30';
  return [
    operationQuestionForRange(currentRange),
    ...quickSuggestions.slice(0, 3),
  ];
});
const hasCompleteQuestion = computed(() => {
  const text = question.value.trim();
  return (
    /[?.!]$/.test(text) || normalizedQuestion.value.split(/\s+/).length >= 7
  );
});
const matchingAgents = computed(() => {
  const query = normalizedQuestion.value;
  if (!query) return [];
  const queryTerms = query.split(/\s+/).filter(term => term.length > 2);

  return agents.value
    .filter(agent => {
      const name = normalize(agent.name);
      const nameTerms = name.split(/\s+/).filter(term => term.length > 2);

      return (
        name.includes(query) ||
        query.includes(name) ||
        queryTerms.some(queryTerm =>
          nameTerms.some(nameTerm => nameTerm.includes(queryTerm))
        )
      );
    })
    .slice(0, 6);
});
const selectedAgent = computed(() =>
  matchingAgents.value.length === 1 ? matchingAgents.value[0] : null
);
const shouldSuggestAgents = computed(() => {
  const query = normalizedQuestion.value;
  return (
    matchingAgents.value.length > 0 ||
    /atend|agent|desempenho|relatorio/.test(query)
  );
});
const selectedAgentSuggestions = agent => [
  {
    key: `agent-performance-${agent.id}`,
    kind: 'agent-action',
    label: `${copy.completePerformance} ${agent.name}`,
    detail: copy.agentPrefix,
    value: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_PERFORMANCE_QUESTION', {
      name: agent.name,
    }),
  },
  {
    key: `agent-conversations-${agent.id}`,
    kind: 'agent-action',
    label: `${copy.conversationsAndResolutions} ${agent.name}`,
    detail: copy.agentPrefix,
    value: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_CONVERSATIONS_QUESTION', {
      name: agent.name,
    }),
  },
  {
    key: `agent-response-time-${agent.id}`,
    kind: 'agent-action',
    label: `${copy.responseTime} ${agent.name}`,
    detail: copy.agentPrefix,
    value: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_RESPONSE_TIME_QUESTION', {
      name: agent.name,
    }),
  },
  {
    key: `agent-comparison-${agent.id}`,
    kind: 'agent-action',
    label: `${copy.compareWithTeam}: ${agent.name}`,
    detail: copy.agentPrefix,
    value: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_COMPARISON_QUESTION', {
      name: agent.name,
    }),
  },
];
const agentSelectionSuggestions = agentsList =>
  agentsList.map(agent => ({
    key: `agent-${agent.id}`,
    kind: 'agent',
    label: agent.name,
    detail: copy.agentPrefix,
    value: t('OVERVIEW_REPORTS.ANALYTICS.AGENT_PERFORMANCE_QUESTION', {
      name: agent.name,
    }),
  }));
const agentSuggestionsForQuestion = () => {
  if (selectedAgent.value) return selectedAgentSuggestions(selectedAgent.value);
  if (!shouldSuggestAgents.value) return [];

  const agentsList = matchingAgents.value.length
    ? matchingAgents.value
    : agents.value.slice(0, 6);
  return agentSelectionSuggestions(agentsList);
};
const recentQuestionSuggestions = computed(() => {
  const query = normalizedQuestion.value;
  if (hasCompleteQuestion.value) return [];

  return recentQuestions.value
    .filter(item => !query || normalize(item).includes(query))
    .map(item => ({
      key: `recent-${item}`,
      kind: 'recent',
      label: item,
      value: item,
    }));
});
const recommendedQuestionSuggestions = computed(() => {
  const query = normalizedQuestion.value;
  if (hasCompleteQuestion.value) return [];

  return quickSuggestions
    .filter(suggestion => !query || normalize(suggestion).includes(query))
    .map(suggestion => ({
      key: `recommended-${suggestion}`,
      kind: 'question',
      label: suggestion,
      value: suggestion,
    }));
});
const autocompleteSuggestions = computed(() => {
  if (hasCompleteQuestion.value) return [];

  const agentSuggestions = agentSuggestionsForQuestion();
  const recommendations = selectedAgent.value
    ? []
    : recommendedQuestionSuggestions.value;

  return [
    ...agentSuggestions,
    ...recentQuestionSuggestions.value,
    ...recommendations,
  ].slice(0, 10);
});
const suggestionSections = computed(() => {
  const suggestions = autocompleteSuggestions.value;
  const agentsList = suggestions.filter(suggestion =>
    suggestion.kind.startsWith('agent')
  );
  const recentList = suggestions.filter(
    suggestion => suggestion.kind === 'recent'
  );
  const recommendedList = suggestions.filter(
    suggestion => suggestion.kind === 'question'
  );

  return [
    { key: 'agents', title: copy.suggestionsTitle, items: agentsList },
    { key: 'recent', title: copy.recentQuestionsTitle, items: recentList },
    {
      key: 'recommended',
      title: copy.recommendedQuestionsTitle,
      items: recommendedList,
    },
  ].filter(section => section.items.length);
});
const canSubmit = computed(
  () => question.value.trim().length > 0 && !isLoading.value
);
const selectSuggestion = suggestion => {
  question.value = suggestion.value;
  isSuggestionsOpen.value = false;
  activeSuggestionIndex.value = -1;
};
const moveSuggestion = direction => {
  if (!autocompleteSuggestions.value.length) return;

  isSuggestionsOpen.value = true;
  activeSuggestionIndex.value =
    (activeSuggestionIndex.value +
      direction +
      autocompleteSuggestions.value.length) %
    autocompleteSuggestions.value.length;
};
const selectActiveSuggestion = event => {
  const suggestion = autocompleteSuggestions.value[activeSuggestionIndex.value];
  if (!suggestion) return;

  event.preventDefault();
  selectSuggestion(suggestion);
};
const onQuestionInput = () => {
  isSuggestionsOpen.value = true;
  activeSuggestionIndex.value = -1;
};
const closeSuggestions = () => {
  isSuggestionsOpen.value = false;
  activeSuggestionIndex.value = -1;
};
const saveRecentQuestion = value => {
  const item = value.trim();
  if (!item) return;

  recentQuestions.value = [
    item,
    ...recentQuestions.value.filter(questionItem => questionItem !== item),
  ].slice(0, MAX_RECENT_QUESTIONS);
  window.localStorage.setItem(
    QUESTION_HISTORY_STORAGE_KEY,
    JSON.stringify(recentQuestions.value)
  );
};
const removeRecentQuestion = value => {
  recentQuestions.value = recentQuestions.value.filter(item => item !== value);
  window.localStorage.setItem(
    QUESTION_HISTORY_STORAGE_KEY,
    JSON.stringify(recentQuestions.value)
  );
  activeSuggestionIndex.value = -1;
};
const waitForInsight = async (insightId, attempt = 0) => {
  if (attempt >= 900)
    throw new Error(t('OVERVIEW_REPORTS.ANALYTICS.ANALYSIS_TIMEOUT'));

  const { data } = await ReportsAPI.getLumeraInsightStatus(insightId);
  if (data.status === 'completed' && data.ready) return data.analysis;
  if (data.status === 'failed') {
    throw new Error(data.error_message || copy.unavailable);
  }

  processingMessage.value = copy.processing;
  await new Promise(resolve => {
    setTimeout(resolve, 1000);
  });
  return waitForInsight(insightId, attempt + 1);
};
const ask = async value => {
  question.value = value || question.value;
  if (!canSubmit.value) return;

  error.value = '';
  processingMessage.value = '';
  const filters = {
    ...periodFromRange(),
    criterion: criterion.value,
    comparison: comparison.value,
    comparisonAgentId: comparisonAgentId.value || undefined,
    goal: goal.value || undefined,
  };
  if (exceedsMaximumPeriod(filters)) {
    error.value = copy.maximumPeriod;
    return;
  }
  isLoading.value = true;
  emit('loading');
  try {
    const { data: insight } = await ReportsAPI.askLumera({
      question: question.value,
      ...filters,
    });
    const data = await waitForInsight(insight.id);
    saveRecentQuestion(question.value);
    emit('analysis', {
      ...data,
      query: { question: question.value, filters },
    });
  } catch (requestError) {
    error.value = requestError.response?.data?.error || copy.unavailable;
  } finally {
    isLoading.value = false;
    processingMessage.value = '';
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  try {
    const storedQuestions = JSON.parse(
      window.localStorage.getItem(QUESTION_HISTORY_STORAGE_KEY) || '[]'
    );
    recentQuestions.value = Array.isArray(storedQuestions)
      ? storedQuestions
          .filter(item => typeof item === 'string')
          .slice(0, MAX_RECENT_QUESTIONS)
      : [];
  } catch {
    recentQuestions.value = [];
  }
});

watch(
  () => props.savedReport,
  report => {
    if (!report) return;

    question.value = report.question;
    const { since, until } = report.filters || {};
    if (since && until) {
      selectedRange.value = 'custom';
      customCalendarRangeType.value = 'custom';
      customDateRange.value = normalizeCustomDateRange([
        new Date(since * 1000),
        new Date(until * 1000),
      ]);
    }
    criterion.value = report.filters?.criterion || 'auto';
    comparison.value = report.filters?.comparison || 'previous_period';
    ask();
  }
);
</script>

<template>
  <section class="rounded-2xl border border-n-strong bg-n-alpha-2 p-5 sm:p-6">
    <div class="flex gap-4">
      <div
        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-500"
      >
        <i class="i-lucide-sparkles text-2xl" />
      </div>
      <div>
        <h2 class="text-heading-2 text-n-slate-12">{{ copy.title }}</h2>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ copy.description }}
        </p>
      </div>
    </div>
    <form class="mt-5" autocomplete="off" @submit.prevent="ask()">
      <label class="sr-only" for="lumera-insight-question">
        {{ copy.questionLabel }}
      </label>
      <div v-on-clickaway="closeSuggestions" class="relative">
        <input
          id="lumera-insight-question"
          v-model="question"
          maxlength="1200"
          type="search"
          name="lumera-ai-question"
          autocomplete="off"
          class="w-full rounded-xl border border-n-strong bg-n-surface-1 px-4 py-4 text-sm text-n-slate-12 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          :class="{ 'cursor-not-allowed opacity-60': isLoading }"
          :placeholder="copy.placeholder"
          :disabled="isLoading"
          @focus="isSuggestionsOpen = true"
          @input="onQuestionInput"
          @keydown.down.prevent="moveSuggestion(1)"
          @keydown.up.prevent="moveSuggestion(-1)"
          @keydown.enter="selectActiveSuggestion"
          @keydown.esc="isSuggestionsOpen = false"
        />
        <div
          v-if="isSuggestionsOpen && autocompleteSuggestions.length"
          class="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-n-strong bg-n-surface-1 p-1 shadow-xl"
        >
          <template v-for="section in suggestionSections" :key="section.key">
            <p class="mb-1 px-3 pt-2 text-xs font-medium text-n-slate-10">
              {{ section.title }}
            </p>
            <div
              v-for="suggestion in section.items"
              :key="suggestion.key"
              class="flex items-center rounded-lg transition hover:bg-n-alpha-2"
              :class="{
                'bg-n-alpha-2':
                  activeSuggestionIndex ===
                  autocompleteSuggestions.findIndex(
                    item => item.key === suggestion.key
                  ),
              }"
            >
              <button
                type="button"
                class="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left text-sm text-n-slate-12"
                @mousedown.prevent="selectSuggestion(suggestion)"
              >
                <i
                  :class="
                    suggestion.kind.startsWith('agent')
                      ? 'i-lucide-user-round text-blue-500'
                      : suggestion.kind === 'recent'
                        ? 'i-lucide-history text-n-slate-10'
                        : 'i-lucide-sparkles text-blue-500'
                  "
                />
                <span class="flex min-w-0 flex-col gap-0.5 truncate">
                  <span class="truncate">{{ suggestion.label }}</span>
                  <span
                    v-if="suggestion.detail"
                    class="text-xs text-n-slate-10"
                  >
                    {{ suggestion.detail }}
                  </span>
                </span>
              </button>
              <button
                v-if="suggestion.kind === 'recent'"
                type="button"
                class="mr-2 rounded-md p-1.5 text-n-slate-10 transition hover:bg-n-alpha-3 hover:text-n-slate-12"
                :aria-label="copy.removeRecentQuestion"
                :title="copy.removeRecentQuestion"
                @mousedown.prevent.stop="removeRecentQuestion(suggestion.value)"
              >
                <i class="i-lucide-x text-base" />
              </button>
            </div>
          </template>
        </div>
      </div>
      <div class="mt-3 flex flex-wrap items-center gap-2">
        <button
          v-for="option in rangeOptions"
          :key="option.value"
          type="button"
          class="rounded-full border px-3 py-1.5 text-xs transition"
          :class="
            selectedRange === option.value
              ? 'border-blue-500 bg-blue-500/10 text-blue-500'
              : 'border-n-strong text-n-slate-11 hover:text-n-slate-12'
          "
          :disabled="isLoading"
          @click="selectRange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="mt-3 grid gap-3 sm:grid-cols-2">
        <label
          class="rounded-xl border border-n-strong bg-n-surface-1 p-3 transition focus-within:border-blue-500"
        >
          <span
            class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <i class="i-lucide-sliders-horizontal text-blue-500" />
            {{ copy.criterion }}
          </span>
          <select
            v-model="criterion"
            class="mt-2 w-full bg-transparent text-sm font-medium text-n-slate-12 outline-none"
            :disabled="isLoading"
          >
            <option
              v-for="option in criterionOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label
          class="rounded-xl border border-n-strong bg-n-surface-1 p-3 transition focus-within:border-blue-500"
        >
          <span
            class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <i class="i-lucide-git-compare-arrows text-blue-500" />
            {{ copy.comparison }}
          </span>
          <select
            v-model="comparison"
            class="mt-2 w-full bg-transparent text-sm font-medium text-n-slate-12 outline-none"
            :disabled="isLoading"
          >
            <option
              v-for="option in comparisonOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <select
            v-if="comparison === 'agent'"
            v-model="comparisonAgentId"
            class="mt-2 w-full bg-transparent text-sm font-medium text-n-slate-12 outline-none"
            :disabled="isLoading"
          >
            <option value="">{{ copy.selectAgent }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
      </div>
      <label class="mt-3 block text-xs font-medium text-n-slate-11">
        {{ copy.goal }}
        <input
          v-model="goal"
          min="1"
          type="number"
          :placeholder="copy.goalPlaceholder"
          :disabled="isLoading"
          class="mt-2 w-full rounded-lg border border-n-strong bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-60"
        />
      </label>
      <div
        v-if="selectedRange === 'custom'"
        class="mt-3"
        :class="{ 'pointer-events-none opacity-60': isLoading }"
      >
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="customCalendarRangeType"
          :show-quick-ranges="false"
          :max-range-days="MAX_ANALYTICAL_PERIOD_DAYS"
          @date-range-changed="onCustomDateRangeChange"
        />
        <p class="mb-0 mt-2 text-xs text-n-slate-10">
          {{ copy.customDateHint }}
        </p>
      </div>
      <div
        class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex flex-wrap gap-2">
          <button
            v-for="suggestion in visibleQuickSuggestions"
            :key="suggestion"
            type="button"
            class="rounded-full border border-n-strong px-3 py-1.5 text-xs text-n-slate-11 transition hover:border-blue-500 hover:text-blue-500"
            :disabled="isLoading"
            @click="ask(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
        <Button
          type="submit"
          :label="copy.submit"
          icon="i-lucide-arrow-up-right"
          color="blue"
          class="min-w-[10.5rem] whitespace-nowrap"
          :is-loading="isLoading"
          :disabled="!canSubmit"
        />
      </div>
    </form>

    <p v-if="processingMessage" class="mb-0 mt-4 text-sm text-slate-400">
      {{ processingMessage }}
    </p>
    <p v-if="error" class="mb-0 mt-4 text-sm text-red-500">{{ error }}</p>
  </section>
</template>
